"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";

// The demo presenter: the site's character standing full height beside the
// stage, like a person demonstrating a phone. Mirrors the proven HeroCharacter
// setup: same paint, the cap and headphones re-attached to the head bone so
// they stay in sync with the face through every clip, and the pointer look-at
// so it feels alive. Mode drives the clip (idle, talk, wave, pick); facing
// turns the body toward the viewer or toward the stage. One unified fallback
// (WebGL missing or failed, load failure, reduced motion, low end device)
// renders the provided icon in the same reserved slot.
export default function DemoHost({ mode, facing, fallback }: { mode: "idle" | "talk" | "wave" | "pick"; facing: "viewer" | "stage"; fallback: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [usable, setUsable] = useState<boolean | null>(null);
  const [seen, setSeen] = useState(false);
  const apiRef = useRef<{ play: (m: string) => void; face: (f: string) => void } | null>(null);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const mem = (navigator as unknown as { deviceMemory?: number }).deviceMemory;
    const lowEnd = typeof mem === "number" && mem < 4;
    let webgl = false;
    try {
      const t = document.createElement("canvas");
      webgl = Boolean(t.getContext("webgl") || t.getContext("webgl2"));
    } catch { webgl = false; }
    if (reduced || lowEnd || !webgl) { setUsable(false); return; }
    setUsable(true);
  }, []);

  // Lazy mount: nothing heavy is created until the slot is on screen. When
  // IntersectionObserver is unavailable, mount immediately.
  useEffect(() => {
    if (!usable || seen) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setSeen(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } }, { threshold: 0.01 });
    io.observe(el);
    return () => io.disconnect();
  }, [usable, seen]);

  useEffect(() => {
    if (!usable || !seen) return;
    const el = ref.current;
    if (!el) return;

    let disposed = false;
    let frameId = 0;
    let lastT = 0;
    let mixer: THREE.AnimationMixer | null = null;
    const actions: Record<string, THREE.AnimationAction> = {};
    let current: THREE.AnimationAction | null = null;
    let model: THREE.Object3D | null = null;
    let headBone: THREE.Object3D | null = null;

    // Body turn (viewer vs stage) and head look-at state. The look offset is
    // applied non accumulatively: each frame the mixer-authored head pose is
    // captured as the base, and the offset multiplies a COPY of it, so clips
    // that do not key the head every frame can never drift or twist.
    let targetBodyY = 0;
    let bodyY = 0;
    let targetLookX = 0, targetLookY = 0, lookX = 0, lookY = 0;
    const lookEuler = new THREE.Euler();
    const lookQuat = new THREE.Quaternion();
    const baseQuat = new THREE.Quaternion();
    const lastApplied = new THREE.Quaternion();
    let haveApplied = false;

    const w = el.clientWidth || 220;
    const h = el.clientHeight || 300;
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(w, h);
      renderer.setClearColor(0x000000, 0);
      el.appendChild(renderer.domElement);
    } catch {
      setUsable(false);
      return;
    }

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(30, w / h, 0.1, 100);
    scene.add(new THREE.AmbientLight(0xffffff, 1.7));
    const key = new THREE.DirectionalLight(0xffffff, 1.4);
    key.position.set(2, 4, 3);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xffe9d8, 0.7);
    fill.position.set(-3, 2, 1);
    scene.add(fill);
    const front = new THREE.DirectionalLight(0xffffff, 1.1);
    front.position.set(0, 1.4, 5);
    scene.add(front);

    let charH = 1.8;
    let charW = 0.7;

    // Frame the full standing character: feet just above the slot bottom,
    // headroom for the wave, side room so turns never clip.
    function fitCamera(cw: number, ch: number) {
      const aspect = cw / ch;
      camera.aspect = aspect;
      const vfov = THREE.MathUtils.degToRad(camera.fov);
      const yB = -charH * 0.02;
      const yT = charH * 1.18;
      const winH = yT - yB;
      const winW = charW * 1.6;
      const cy = (yB + yT) / 2;
      const distV = winH / 2 / Math.tan(vfov / 2);
      const distH = winW / 2 / Math.tan(vfov / 2) / aspect;
      const dist = Math.max(distV, distH);
      camera.position.set(0, cy, dist);
      camera.lookAt(0, cy, 0);
      camera.updateProjectionMatrix();
    }
    fitCamera(w, h);

    const draco = new DRACOLoader();
    draco.setDecoderPath("/draco/");
    const loader = new GLTFLoader();
    loader.setDRACOLoader(draco);

    loader.load(
      "/models/character.glb",
      (gltf) => {
        if (disposed) {
          // Late load after unmount: free the asset instead of leaking it.
          gltf.scene.traverse((o) => {
            const m = o as THREE.Mesh;
            if (m.isMesh || (m as unknown as THREE.SkinnedMesh).isSkinnedMesh) {
              m.geometry?.dispose();
              (Array.isArray(m.material) ? m.material : [m.material]).forEach((mat) => (mat as THREE.Material).dispose());
            }
          });
          return;
        }
        model = gltf.scene;
        scene.add(model);
        const box = new THREE.Box3().setFromObject(model);
        model.position.x -= (box.min.x + box.max.x) / 2;
        model.position.y -= box.min.y;
        model.position.z -= (box.min.z + box.max.z) / 2;
        model.updateMatrixWorld(true);
        model.traverse((o) => {
          const m = o as THREE.Mesh;
          if (!(m.isMesh || (m as unknown as THREE.SkinnedMesh).isSkinnedMesh)) return;
          const nm = m.name.toLowerCase();
          const paint = (hex: string) => {
            const src = Array.isArray(m.material) ? m.material[0] : m.material;
            const mat = (src as THREE.MeshStandardMaterial).clone();
            mat.map = null;
            mat.color.set(hex);
            mat.emissive = new THREE.Color(hex);
            mat.emissiveIntensity = 0.16;
            mat.roughness = 0.7;
            mat.metalness = 0;
            mat.needsUpdate = true;
            m.material = mat;
          };
          if (nm === "body") paint("#ff7a4d");
          else if (nm === "cap" || nm === "headphones") paint("#f0552e");
        });

        // The cap and headphones ship as loose meshes; attach them to the head
        // bone so they move with the face in every clip and head turn. If the
        // export ever renames these nodes, fall back to the icon rather than
        // silently showing a desynced character.
        headBone = model.getObjectByName("head") ?? null;
        const cap = model.getObjectByName("cap");
        const hp = model.getObjectByName("headphones");
        if (!headBone || !cap || !hp) {
          setUsable(false);
          return;
        }
        headBone.attach(cap);
        headBone.attach(hp);
        baseQuat.copy(headBone.quaternion);

        const box2 = new THREE.Box3().setFromObject(model);
        const size = box2.getSize(new THREE.Vector3());
        charH = size.y;
        charW = Math.max(size.x, size.z);
        fitCamera(el.clientWidth || w, el.clientHeight || h);

        mixer = new THREE.AnimationMixer(model);
        for (const name of ["Idle", "Talk", "Wave", "Pick"]) {
          const clip = gltf.animations.find((a) => a.name === name);
          if (clip) actions[name.toLowerCase()] = mixer.clipAction(clip);
        }
        for (const oneShot of ["wave", "pick"]) {
          actions[oneShot]?.setLoop(THREE.LoopOnce, 1);
        }
        mixer.addEventListener("finished", () => {
          if (actions.idle && current !== actions.idle) {
            actions.idle.reset().play();
            if (current) actions.idle.crossFadeFrom(current, 0.3, true);
            current = actions.idle;
          }
        });
        apiRef.current = {
          play: (m: string) => {
            const nextA = actions[m] || actions.idle;
            if (!nextA || nextA === current) return;
            nextA.reset().play();
            if (current) nextA.crossFadeFrom(current, 0.25, true);
            current = nextA;
          },
          face: (f: string) => {
            // Toward the stage on its right, or back square to the viewer.
            targetBodyY = f === "stage" ? 0.55 : -0.06;
          },
        };
        actions.idle?.play();
        current = actions.idle || null;
        apiRef.current.play(mode);
        apiRef.current.face(facing);
      },
      undefined,
      () => { if (!disposed) setUsable(false); },
    );

    // Pointer look-at, same as the apply page robot: the head follows the
    // cursor or touch, which is most of what makes it feel alive.
    const aim = (clientX: number, clientY: number) => {
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height * 0.35;
      const dx = (clientX - cx) / window.innerWidth;
      const dy = (clientY - cy) / window.innerHeight;
      targetLookY = THREE.MathUtils.clamp(dx * 1.4, -0.45, 0.45);
      targetLookX = THREE.MathUtils.clamp(dy * 1.0, -0.26, 0.34);
    };
    const onMouse = (e: MouseEvent) => aim(e.clientX, e.clientY);
    const onTouch = (e: TouchEvent) => { if (e.touches[0]) aim(e.touches[0].clientX, e.touches[0].clientY); };
    window.addEventListener("mousemove", onMouse, { passive: true });
    window.addEventListener("touchmove", onTouch, { passive: true });

    const onResize = () => {
      const nw = el.clientWidth, nh = el.clientHeight;
      if (!nw || !nh) return;
      renderer.setSize(nw, nh);
      fitCamera(nw, nh);
    };
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(onResize);
      ro.observe(el);
    } else {
      window.addEventListener("resize", onResize, { passive: true });
    }

    let onScreen = true;
    let io: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(([e]) => { onScreen = e.isIntersecting; }, { threshold: 0.01 });
      io.observe(el);
    }

    function animate(t: number) {
      frameId = requestAnimationFrame(animate);
      const dt = Math.min((t - lastT) / 1000, 0.05);
      lastT = t;
      if (!onScreen || document.hidden) return;
      mixer?.update(dt);
      if (model) {
        bodyY += (targetBodyY - bodyY) * 0.08;
        model.rotation.y = bodyY;
      }
      lookX += (targetLookX - lookX) * 0.12;
      lookY += (targetLookY - lookY) * 0.12;
      if (headBone) {
        // If the mixer re-keyed the head this frame, its quaternion differs
        // from what we last applied: capture it as the new base. Otherwise
        // keep the previous base, so the offset never accumulates.
        if (!haveApplied || !headBone.quaternion.equals(lastApplied)) {
          baseQuat.copy(headBone.quaternion);
        }
        lookEuler.set(lookX, lookY, 0, "XYZ");
        lookQuat.setFromEuler(lookEuler);
        headBone.quaternion.copy(baseQuat).multiply(lookQuat);
        lastApplied.copy(headBone.quaternion);
        haveApplied = true;
      }
      renderer.render(scene, camera);
    }
    frameId = requestAnimationFrame(animate);

    const disposeModel = (root: THREE.Object3D) => {
      root.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!(m.isMesh || (m as unknown as THREE.SkinnedMesh).isSkinnedMesh)) return;
        m.geometry?.dispose();
        const mats = Array.isArray(m.material) ? m.material : [m.material];
        mats.forEach((mat) => {
          const std = mat as THREE.MeshStandardMaterial;
          std.map?.dispose();
          std.dispose();
        });
      });
    };

    return () => {
      disposed = true;
      cancelAnimationFrame(frameId);
      window.removeEventListener("mousemove", onMouse);
      window.removeEventListener("touchmove", onTouch);
      window.removeEventListener("resize", onResize);
      ro?.disconnect();
      io?.disconnect();
      if (model) disposeModel(model);
      renderer.dispose();
      draco.dispose();
      if (el.contains(renderer.domElement)) el.removeChild(renderer.domElement);
      apiRef.current = null;
    };
  }, [usable, seen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { apiRef.current?.play(mode); }, [mode]);
  useEffect(() => { apiRef.current?.face(facing); }, [facing]);

  if (usable === false) return <span className="ds-presenter-fallback" aria-hidden="true">{fallback}</span>;
  return <div ref={ref} className="ds-presenter-canvas" aria-hidden="true" />;
}
